import React, { useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { TFunction } from 'react-i18next';
import { DebouncedFunc, debounce } from 'lodash';
import {
  Button,
  Card,
  List,
  Pagination,
  Stack,
  StyledContainer,
  Text,
  TextField,
  withTranslation,
} from '@piwikpro/ui-components';
import {
  ConfigService,
  errorBoundary,
  InternalListLink,
  Pagination as PaginationManager,
  withInject,
} from '@piwikpro/platform';
import { RouterService } from '@piwikpro/router-crate';
import { AccessControlService } from '@piwikpro/access-control-crate';
import { UserGroups as UserGroupsService } from '@piwikpro/user-groups-crate';
import { UserGroupMembershipsState } from '@piwikpro/access-control-crate/redux/userGroupMemberships';
import { AdministrationState } from '../../../../../redux';
import { persistListLimit, readListLimit, USER_GROUP_MEMBERSHIPS_LIST_ID } from './listLimit';

const mapStateToProps = (state: AdministrationState & {
  accessControl: { userGroupMemberships: UserGroupMembershipsState }
}) => ({
  user: state.users.details.user.details as NonNullable<
  AdministrationState['users']['details']['user']['details']
  >,
  items: state.accessControl.userGroupMemberships?.items ?? [],
  total: state.accessControl.userGroupMemberships?.total ?? 0,
  isLoading: state.accessControl.userGroupMemberships?.isLoading ?? false,
  failed: state.accessControl.userGroupMemberships?.failed ?? false,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  accessControl: AccessControlService
  userGroups: UserGroupsService
  userPreferences: any
  routerService: RouterService
  config: ConfigService
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
  membershipRevision: number
  onMembershipsChanged: () => void
}

const UserGroups: React.FC<Props> = ({
  accessControl,
  userGroups,
  userPreferences,
  routerService,
  config,
  user,
  items,
  total,
  isLoading,
  failed,
  membershipRevision,
  onMembershipsChanged,
  t,
}) => {
  const defaultLimit = config.get<number>('PAGINATION_ITEMS_PER_PAGE');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('name');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => readListLimit(
    userPreferences,
    USER_GROUP_MEMBERSHIPS_LIST_ID,
    defaultLimit,
  ));
  const searchDebounceRef = useRef<DebouncedFunc<any> | null>(null);

  const fetchGroups = async (next: { page: number, limit: number, search: string, sort: string }) => {
    try {
      const resp = await accessControl.fetchUserGroupMemberships(user.id, {
        offset: (next.page - 1) * next.limit,
        limit: next.limit,
        q: next.search,
        sort: next.sort,
      });

      const lastPage = Math.max(1, Math.ceil(resp.total / next.limit));

      if (next.page > lastPage) {
        setPage(lastPage);
        await fetchGroups({ ...next, page: lastPage });
      }
    } catch {
      // handled in redux
    }
  };

  useEffect(() => {
    if (membershipRevision === 0) {
      return;
    }

    fetchGroups({
      page, limit, search, sort,
    });
  }, [membershipRevision]);

  useEffect(() => {
    searchDebounceRef.current?.cancel();

    const storedLimit = readListLimit(
      userPreferences,
      USER_GROUP_MEMBERSHIPS_LIST_ID,
      defaultLimit,
    );

    setSearch('');
    setSort('name');
    setPage(1);
    setLimit(storedLimit);
    fetchGroups({
      page: 1,
      limit: storedLimit,
      search: '',
      sort: 'name',
    });

    return () => {
      searchDebounceRef.current?.cancel();
    };
  }, [user.id]);

  const changePage = (nextPage: number) => {
    setPage(nextPage);
    fetchGroups({
      page: nextPage,
      limit,
      search,
      sort,
    });
  };

  const handleLimitChange = (nextLimit: number) => {
    persistListLimit(userPreferences, USER_GROUP_MEMBERSHIPS_LIST_ID, nextLimit);
    setLimit(nextLimit);
    setPage(1);
    fetchGroups({
      page: 1,
      limit: nextLimit,
      search,
      sort,
    });
  };

  const handlePageChange = (offset: number) => {
    changePage(Math.floor(offset / limit) + 1);
  };

  const handleSearchChange = (event) => {
    const nextSearch = event.target.value;

    searchDebounceRef.current?.cancel();

    const handleSearch = debounce(() => {
      setPage(1);
      fetchGroups({
        page: 1,
        limit,
        search: nextSearch,
        sort,
      });
    }, config.get('LIST_SEARCH_DELAY'));

    searchDebounceRef.current = handleSearch;
    setSearch(nextSearch);
    handleSearch();
  };

  const handleSortChange = () => {
    const nextSort = sort === 'name' ? '-name' : 'name';
    setSort(nextSort);
    setPage(1);
    fetchGroups({
      page: 1,
      limit,
      search,
      sort: nextSort,
    });
  };

  const handleRemove = async (groupId: string) => {
    try {
      await userGroups.deleteMemberFromUserGroup(groupId, user.id, { toast: 'user-updated' });
    } catch {
      return;
    }

    const isLastOnPage = items.length === 1 && page > 1;
    const nextPage = isLastOnPage ? page - 1 : page;

    if (isLastOnPage) {
      setPage(nextPage);
    }

    onMembershipsChanged();
  };

  const pagination = new PaginationManager(items, total, limit, page);

  return (
    <Card.Section subdued>
      <Stack vertical spacing="wide">
        <Stack.Item fill>
          <Stack verticalAlignment="center">
            <Stack.Item fill>
              <Stack vertical spacing="narrow">
                <Stack.Item>
                  <Text as="h2" size={16} weight={700} wrapType="noWrapAndCutText">
                    {t('users.groups.title')}
                  </Text>
                </Stack.Item>
                <Stack.Item previousSpacing="extraNarrow">
                  <Text color="InkLighter" as="p">{t('users.groups.description')}</Text>
                </Stack.Item>
              </Stack>
            </Stack.Item>
            <Stack.Item>
              <Button
                appearance="simple"
                icon="plus"
                text={t('users.groups.add.button')}
                onClick={() => routerService.openAdministrationOverlay('/m/add-to-group')}
              />
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item fill previousSpacing="default">
          {!failed && (
            <StyledContainer
              border={['left', 'right', 'top']}
              padding="narrow"
              backgroundColor="gray"
            >
              <TextField
                name="search"
                icon="search"
                placeholder={t('users.groups.searchPlaceholder')}
                value={search}
                onChange={handleSearchChange}
                clearable
              />
            </StyledContainer>
          )}
          <List
            columns={[
              {
                size: 7,
                align: 'left',
                name: (
                  <List.SortableHeader
                    text={t('users.groups.column.group')}
                    selected
                    order={sort === 'name' ? 'asc' : 'dsc'}
                    onClick={handleSortChange}
                  />
                ),
              },
              {
                size: 1,
                align: 'right',
              },
            ]}
            error={failed}
            isLoading={isLoading}
          >
            {!failed && items.map(group => (
              <List.Item key={group.id}>
                <InternalListLink
                  to="/administration/user-groups/$groupId"
                  params={{ groupId: group.id }}
                  label={group.name}
                />
                <Button
                  appearance="simple"
                  text={t('users.groups.remove')}
                  onClick={() => handleRemove(group.id)}
                />
              </List.Item>
            ))}
          </List>
        </Stack.Item>
        {(!failed && items.length > 0) && (
          <Stack.Item fill previousSpacing="narrow">
            <Pagination
              onLimitChange={handleLimitChange}
              limit={pagination.getLimit()}
              total={total}
              limitOptions={config.get('PAGINATION_ITEMS_PER_PAGE_OPTIONS')}
              startIndex={pagination.offset}
              endIndex={pagination.offset + pagination.getLimit()}
              onPageChange={handlePageChange}
            />
          </Stack.Item>
        )}
      </Stack>
    </Card.Section>
  );
};

export default connector(withInject<InjectedProps>({
  accessControl: 'AccessControlCrate.accessControl',
  userGroups: 'UserGroupsCrate.userGroups',
  userPreferences: 'userPreferences',
  routerService: 'RouterCrate.router',
  config: 'config',
})(withTranslation('administration')(errorBoundary(UserGroups))));
